<template>
  <HeaderBar />
  <ButtonBackContainer>
    <ButtonBack :fallback="{ name: 'bookingPlan' }">{{ t('back') }}</ButtonBack>
    <CreatedInfoBlock
      v-if="block && $can('read', 'AuditLog')"
      :entity-type="AuditLogEntityType.ALLOCATION"
      :entity-id="block?.id"
    />
  </ButtonBackContainer>

  <section class="carvia-container">
    <MaintenanceForm
      v-if="car && (block || blockNew)"
      :car="car"
      :block="block"
      :block-new="blockNew"
      :start-date="startDate"
      :is-saving="isCreating || isUpdating"
      :is-deleting="isDeleting"
      :is-migrating="isMigrating"
      :can-migrate="canMigrate"
      @on-submit="handleSubmit"
      @on-delete="onDelete"
      @on-migrate="onMigrate"
    />
    <div v-else class="flex justify-center">
      <Spinner />
    </div>
  </section>
</template>

<script setup lang="ts">
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import MaintenanceForm from './MaintenanceForm.vue';
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import { useCar } from '@/queries/use-cars';
import { useUiStore } from '@/stores/ui.store';
import { computed, ref, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { BookingLocationType } from '@/entities/bookings/booking-location-type.enum';
import { Alert } from '@/services/alert.service';
import ButtonBackContainer from '@/components/ButtonBackContainer.vue';
import { DateTime } from 'luxon';
import {
  useAllocation,
  useCreateAllocation,
  useDeleteAllocation,
  useUpdateAllocation,
} from '@/queries/use-allocations.ts';
import {
  AllocationType,
  AuditLogEntityType,
  type CreateAllocationDto,
  type MigrateBlockerToWorkshopAppointmentDto,
  type UpdateAllocationDto,
  WorkshopAppointmentType,
} from '@carvia/ros-client-types';
import CreatedInfoBlock from '@/components/audit-log/CreatedInfoBlock.vue';
import { useAbility } from '@casl/vue';
import { useMigrateBlockerToWorkshopAppointment } from '@/queries/use-workshop-appointments';

const { t } = useI18n();
const { can } = useAbility();
const uiStore = useUiStore();
const route = useRoute();
const router = useRouter();
const carId = route.params.carId as string;
const blockId = ref(route.params.blockId as string);
const blockNew = ref(blockId.value === 'new');
const startDate = route.query.startDate
  ? DateTime.fromISO(route.query.startDate as string)
  : undefined;
const blockFetchingEnabled = computed(() => !blockNew.value);
const { data: car } = useCar(carId);
const { data: block } = useAllocation(blockId, blockFetchingEnabled);

watchEffect(() => {
  uiStore.setHeaderTitle('Block', car.value?.vehicleType.make.name, car.value?.vehicleType.model);
});

const { isPending: isCreating, mutateAsync: createBlocker } = useCreateAllocation();
const { isPending: isUpdating, mutateAsync: updateBlocker } = useUpdateAllocation();
const { isPending: isDeleting, mutateAsync: deleteBlocker } = useDeleteAllocation();
const { isPending: isMigrating, mutateAsync: migrateBlockerToWorkshopAppointment } =
  useMigrateBlockerToWorkshopAppointment();

const canMigrate = computed(
  () =>
    !blockNew.value &&
    can('create', 'WorkshopAppointment') &&
    block.value?.type === AllocationType.BLOCKER,
);

const handleSubmit = async (allocation: UpdateAllocationDto) => {
  if (blockNew.value) {
    const createBlockerValues = {
      ...allocation,
      carId: carId,
      type: AllocationType.BLOCKER,
      pickupLocationType: BookingLocationType.STATION,
      dropoffLocationType: BookingLocationType.STATION,
    };
    const { id } = await createBlocker(createBlockerValues as CreateAllocationDto);
    await router.replace({
      name: 'maintenanceEdit',
      params: { carId: car.value?.id, blockId: id },
    });
    blockId.value = id;
    blockNew.value = false;
  } else {
    await updateBlocker({ id: blockId.value, allocation });
  }
};

const onMigrate = async () => {
  if (!block.value?.id) return;
  const alertResult = await Alert.fire({
    titleText: t('convertToWorkshopAppointmentTitle'),
    text: t('convertToWorkshopAppointmentText'),
    icon: 'question',
    showDenyButton: true,
    confirmButtonText: t('convert'),
    denyButtonText: t('cancel'),
  });
  if (!alertResult.isConfirmed) return;

  const dto: MigrateBlockerToWorkshopAppointmentDto = {
    types: [WorkshopAppointmentType.OTHER_MAINTENANCE],
    usesCollectAndReturnService: true,
  };
  const workshopAppointment = await migrateBlockerToWorkshopAppointment({
    allocationId: block.value.id,
    dto,
  });
  await router.push({ name: 'editWorkshopAppointment', params: { id: workshopAppointment.id } });
};

const onDelete = async () => {
  if (!block.value?.id) return;
  const alertResult = await Alert.fire({
    titleText: t('reallyDeleteBlockTitle'),
    text: t('reallyDeleteBlockText'),
    icon: 'warning',
    showDenyButton: true,
    confirmButtonText: t('delete'),
    denyButtonText: t('keep'),
  });
  if (alertResult.isConfirmed) {
    await deleteBlocker(block.value.id);
    await router.push({ name: 'bookingPlan' });
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "comment": "Comment",
    "back": "Back",
    "reallyDeleteBlockTitle": "Really remove Block?",
    "reallyDeleteBlockText": "The Block will be permanently deleted.",
    "keep": "Keep",
    "convertToWorkshopAppointmentTitle": "Convert to workshop appointment?",
    "convertToWorkshopAppointmentText": "This block will be converted into a workshop appointment. You can add details on the next screen.",
    "convert": "Convert",
    "cancel": "Cancel"
  },
  "de": {
    "comment": "Kommentar",
    "back": "Zurück",
    "reallyDeleteBlockTitle": "Block wirklich entfernen?",
    "reallyDeleteBlockText": "Der Block wird unwiderruflich gelöscht.",
    "keep": "Behalten",
    "convertToWorkshopAppointmentTitle": "In Werkstatttermin umwandeln?",
    "convertToWorkshopAppointmentText": "Dieser Block wird in einen Werkstatttermin umgewandelt. Details können auf der nächsten Seite ergänzt werden.",
    "convert": "Umwandeln",
    "cancel": "Abbrechen"
  },
  "es": {
    "comment": "Comentario",
    "back": "Volver",
    "reallyDeleteBlockTitle": "¿Eliminar el bloqueo?",
    "reallyDeleteBlockText": "El bloqueo se eliminará de forma definitiva.",
    "keep": "Conservar",
    "convertToWorkshopAppointmentTitle": "¿Convertir en cita de taller?",
    "convertToWorkshopAppointmentText": "Este bloqueo se convertirá en una cita de taller. Podrás añadir detalles en la siguiente pantalla.",
    "convert": "Convertir",
    "cancel": "Cancelar"
  }
}
</i18n>
